import React, { useCallback, useMemo, useRef } from "react"
import { useFetcher } from "react-router"
import { LoaderCircleIcon, SaveIcon, PlusIcon, TrashIcon, Search, XCircleIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"
import { fetchAPI, handleResponse } from "@/utils"

import type { EnhancedConfigData } from "./brand-lenses-configuration"

export interface IconConfig {
  iconType: "svg" | "character"
  icon: string
  color: string
  [x: string]: unknown
}

export interface ScoreThreshold {
  min: number
  max: number
  icon: IconConfig
  [x: string]: unknown
}

export interface ScoreTemplate {
  presentationType: "percentage"
  thresholds?: ScoreThreshold[]
  [x: string]: unknown
}

export interface ChecksConfig {
  [x: string]: unknown
  view?: false | true | null
  dataViewDefault?: boolean
  hideVerifiedColumn?: boolean
  scoreTemplates?: ScoreTemplate[]
  disableEvaluationEditing?: boolean
  disableDataValueEditing?: boolean
  checkOrder?: string[]
  scoreOrder?: string[]
}

interface CheckSet {
  name: string
}

interface ReferenceItem {
  shortDescription: string
  longDescription: string
  name: string
}

interface DescriptionMapping {
  shortDescription: string
  longDescription: string
  name: string
}

interface FetcherData {
  success?: boolean
  action?: string
  errorMessage?: string
}

interface RawReferenceResponse {
  reference?: string
  shortDescription?: string
  longDescription?: string
}

const AddThresholdDialog = React.memo(({ onAdd }: { onAdd: (threshold: ScoreThreshold) => void }) => {
  const formRef = useRef<HTMLFormElement>(null)
  const [open, setOpen] = React.useState(false)

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (formRef.current == null) return

      const formData = new FormData(formRef.current)
      const minStr = formData.get("min") as string | null
      const maxStr = formData.get("max") as string | null
      const iconTypeStr = formData.get("iconType") as string | null
      const iconStr = formData.get("icon") as string | null
      const colorStr = formData.get("color") as string | null

      const newThreshold: ScoreThreshold = {
        min: parseFloat(minStr ?? "0"),
        max: parseFloat(maxStr ?? "100"),
        icon: {
          iconType: iconTypeStr === "svg" || iconTypeStr === "character" ? iconTypeStr : "character",
          icon: iconStr ?? "",
          color: colorStr ?? "#000000",
        },
      }

      onAdd(newThreshold)
      formRef.current?.reset()
      setOpen(false)
    },
    [onAdd],
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="border-green-200 bg-green-50 hover:bg-green-100">
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Threshold
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Score Threshold</DialogTitle>
        </DialogHeader>
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Min Value</label>
              <Input type="number" name="min" defaultValue="0" min="0" max="100" step="0.1" className="mt-1" required />
            </div>
            <div>
              <label className="text-sm font-medium">Max Value</label>
              <Input
                type="number"
                name="max"
                defaultValue="100"
                min="0"
                max="100"
                step="0.1"
                className="mt-1"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Icon Type</label>
            <Select name="iconType" defaultValue="character">
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="character">Character</SelectItem>
                <SelectItem value="svg">SVG</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Icon</label>
            <Input name="icon" placeholder="Enter character or SVG path..." className="mt-1" required />
          </div>

          <div>
            <label className="text-sm font-medium">Icon Color</label>
            <input
              type="color"
              name="color"
              defaultValue="#000000"
              className="mt-1 h-10 w-full cursor-pointer rounded border"
            />
          </div>

          <div className="flex items-center justify-end gap-4 border-t pt-4">
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit">Add Threshold</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
})

AddThresholdDialog.displayName = "AddThresholdDialog"

const AddReferenceDialog = React.memo(
  ({
    title,
    type,
    clientName,
    brand,
    checkSets,
    isLoadingCheckSets,
    existingReferences,
    onAdd,
  }: {
    title: string
    type: "check" | "score"
    clientName: string
    brand: string
    checkSets: CheckSet[]
    isLoadingCheckSets: boolean
    existingReferences: string[]
    onAdd: (item: ReferenceItem) => void
  }) => {
    const [open, setOpen] = React.useState(false)
    const [selectedCheckSet, setSelectedCheckSet] = React.useState("")
    const [searchTerm, setSearchTerm] = React.useState("")
    const [references, setReferences] = React.useState<ReferenceItem[]>([])
    const [isLoadingRefs, setIsLoadingRefs] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)
    const abortControllerRef = useRef<AbortController | null>(null)

    React.useEffect(() => {
      if (abortControllerRef.current != null) {
        abortControllerRef.current.abort()
      }

      if (selectedCheckSet.length === 0) {
        setReferences([])
        setError(null)
        return
      }

      const controller = new AbortController()
      abortControllerRef.current = controller

      setIsLoadingRefs(true)
      setError(null)

      const endpoint =
        type === "check"
          ? `/api/unityviewer/checks/${clientName}/${brand}/${selectedCheckSet}`
          : `/api/unityviewer/checks/scoretemplates/${clientName}/${brand}/${selectedCheckSet}`

      fetchAPI<RawReferenceResponse[]>(endpoint)
        .then(handleResponse)
        .then((data) => {
          const mapped: ReferenceItem[] = (data ?? []).map((item) => {
            const reference = typeof item.reference === "string" ? item.reference : ""
            return {
              name: reference,
              shortDescription: typeof item.shortDescription === "string" ? item.shortDescription : reference,
              longDescription: typeof item.longDescription === "string" ? item.longDescription : "",
            }
          })
          setReferences(mapped)
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name === "AbortError") {
            console.log(`[${type}] Request aborted`)
          } else {
            setError(`Failed to load ${type}s. Please try again.`)
          }
        })
        .finally(() => {
          setIsLoadingRefs(false)
        })

      return () => {
        controller.abort()
      }
    }, [selectedCheckSet, type, clientName, brand])

    const filteredReferences = useMemo(() => {
      return references.filter((ref) => {
        if (existingReferences.includes(ref.shortDescription)) return false

        if (searchTerm.length > 0) {
          const search = searchTerm.toLowerCase()
          return (
            ref.name.toLowerCase().includes(search) ||
            ref.shortDescription.toLowerCase().includes(search) ||
            ref.longDescription.toLowerCase().includes(search)
          )
        }
        return true
      })
    }, [references, existingReferences, searchTerm])

    const handleAdd = useCallback(
      (item: ReferenceItem) => {
        onAdd(item)
        setOpen(false)
      },
      [onAdd],
    )

    const handleOpenChange = useCallback((newOpen: boolean) => {
      setOpen(newOpen)
      if (!newOpen) {
        setSelectedCheckSet("")
        setSearchTerm("")
        setReferences([])
        setError(null)
        if (abortControllerRef.current != null) {
          abortControllerRef.current.abort()
        }
      }
    }, [])

    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="border-blue-200 bg-info/10 hover:bg-info/20">
            <PlusIcon className="mr-2 h-4 w-4" />
            Add
          </Button>
        </DialogTrigger>
        <DialogContent className="w-[95vw] max-w-2xl sm:w-full">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <div className="flex max-h-[70vh] flex-col gap-4 overflow-hidden">
            <div className="flex-shrink-0">
              <label className="mb-2 block text-sm font-medium">Select Check Set</label>
              <Select
                value={selectedCheckSet}
                onValueChange={(value) => {
                  setSelectedCheckSet(value)
                }}
                disabled={isLoadingCheckSets || isLoadingRefs}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isLoadingCheckSets ? "Loading..." : "Choose a check set..."} />
                </SelectTrigger>
                <SelectContent>
                  {checkSets.length === 0 && !isLoadingCheckSets ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">No check sets available</div>
                  ) : (
                    checkSets.map((checkSet) => (
                      <SelectItem key={checkSet.name} value={checkSet.name}>
                        {checkSet.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedCheckSet.length > 0 && error == null && (
              <>
                <div className="relative flex-shrink-0">
                  <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={`Search ${type}s by name or description...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    disabled={isLoadingRefs}
                  />
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto rounded border">
                  <div className="space-y-2 p-2">
                    {isLoadingRefs ? (
                      <div className="py-8 text-center text-sm text-muted-foreground">
                        <LoaderCircleIcon className="mx-auto mb-2 h-6 w-6 animate-spin" />
                        Loading {type}s...
                      </div>
                    ) : filteredReferences.length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground">
                        {searchTerm.length > 0
                          ? `No ${type}s match your search`
                          : references.length > 0 && existingReferences.length === references.length
                            ? `All ${type}s already added`
                            : `No ${type}s found`}
                      </div>
                    ) : (
                      filteredReferences.map((ref) => (
                        <div
                          key={ref.shortDescription}
                          className="flex flex-col gap-2 rounded-lg border bg-muted p-3 hover:bg-secondary sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium break-words">{ref.name}</div>
                            <div className="text-sm break-words text-muted-foreground">{ref.shortDescription}</div>
                            <div className="text-xs break-words text-muted-foreground">{ref.longDescription}</div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleAdd(ref)}
                            className="w-full flex-shrink-0 sm:ml-3 sm:w-auto"
                          >
                            Add
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            {error != null && (
              <div className="flex-shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end border-t pt-4">
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Close
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    )
  },
)

AddReferenceDialog.displayName = "AddReferenceDialog"

export const ChecksTab = ({ data, config }: { data: EnhancedConfigData; config: ChecksConfig }) => {
  const fetcher = useFetcher<FetcherData>()

  const [checksConfig, setChecksConfig] = React.useState<ChecksConfig>(() => config ?? {})

  const [checkDescriptions, setCheckDescriptions] = React.useState<Record<string, DescriptionMapping>>({})
  const [scoreDescriptions, setScoreDescriptions] = React.useState<Record<string, DescriptionMapping>>({})

  const [checkSets, setCheckSets] = React.useState<CheckSet[]>([])
  const [isLoadingCheckSets, setIsLoadingCheckSets] = React.useState(false)
  const pendingAction = (fetcher.json as { action?: string } | undefined)?.action ?? null

  React.useEffect(() => {
    setIsLoadingCheckSets(true)
    fetchAPI<CheckSet[]>(`/api/unityviewer/checks/sets/${data.loadedOrganisation}/${data.loadedProject}`)
      .then(handleResponse)
      .then((responseData) => {
        setCheckSets(responseData ?? [])
      })
      .catch((error) => {
        console.error("Failed to load check sets:", error)
      })
      .finally(() => {
        setIsLoadingCheckSets(false)
      })
  }, [data.loadedOrganisation, data.loadedProject])

  React.useEffect(() => {
    if (fetcher.data?.success === true && fetcher.data.action === "REMOVE_SECTION") {
      setChecksConfig({})
      setCheckDescriptions({})
      setScoreDescriptions({})
    }
  }, [fetcher.data])

  const handleAddThreshold = useCallback((threshold: ScoreThreshold) => {
    setChecksConfig((prev) => {
      const existingTemplate = prev.scoreTemplates?.[0]
      if (existingTemplate != null) {
        return {
          ...prev,
          scoreTemplates: [
            {
              ...existingTemplate,
              thresholds: [...(existingTemplate.thresholds ?? []), threshold],
            },
          ],
        }
      }
      return {
        ...prev,
        scoreTemplates: [
          {
            presentationType: "percentage",
            thresholds: [threshold],
          },
        ],
      }
    })
  }, [])

  const handleRemoveThreshold = useCallback((templateIndex: number, thresholdIndex: number) => {
    setChecksConfig((prev) => {
      if (prev.scoreTemplates == null) return prev
      return {
        ...prev,
        scoreTemplates: prev.scoreTemplates.map((template, tempIdx) => {
          if (tempIdx !== templateIndex || template.thresholds == null) return template
          return { ...template, thresholds: template.thresholds.filter((_, threshIdx) => threshIdx !== thresholdIndex) }
        }),
      }
    })
  }, [])

  const handleAddCheckOrder = useCallback((item: ReferenceItem) => {
    setChecksConfig((prev) => ({
      ...prev,
      checkOrder: [...(prev.checkOrder ?? []), item.shortDescription],
    }))
    setCheckDescriptions((prev) => ({
      ...prev,
      [item.shortDescription]: {
        shortDescription: item.shortDescription,
        longDescription: item.longDescription,
        name: item.name,
      },
    }))
  }, [])

  const handleRemoveCheckOrder = useCallback((index: number) => {
    setChecksConfig((prev) => ({
      ...prev,
      checkOrder: prev.checkOrder?.filter((_, i) => i !== index),
    }))
  }, [])

  const handleAddScoreOrder = useCallback((item: ReferenceItem) => {
    setChecksConfig((prev) => ({
      ...prev,
      scoreOrder: [...(prev.scoreOrder ?? []), item.shortDescription],
    }))
    setScoreDescriptions((prev) => ({
      ...prev,
      [item.shortDescription]: {
        shortDescription: item.shortDescription,
        longDescription: item.longDescription,
        name: item.name,
      },
    }))
  }, [])

  const handleRemoveScoreOrder = useCallback((index: number) => {
    setChecksConfig((prev) => ({
      ...prev,
      scoreOrder: prev.scoreOrder?.filter((_, i) => i !== index),
    }))
  }, [])

  const handleRemove = useCallback(() => {
    fetcher.submit(
      {
        action: "REMOVE_SECTION",
        organisation: data.loadedOrganisation,
        project: data.loadedProject,
        jsonPath: "$.checks",
        concurrency: data.existingConfig?.concurrency ?? "",
      },
      { method: "post", encType: "application/json" },
    )
  }, [fetcher, data])

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      const value: ChecksConfig = {
        view: checksConfig.view === undefined ? false : checksConfig.view,
        dataViewDefault: checksConfig.dataViewDefault ?? false,
        hideVerifiedColumn: checksConfig.hideVerifiedColumn ?? false,
        disableEvaluationEditing: checksConfig.disableEvaluationEditing ?? false,
        disableDataValueEditing: checksConfig.disableDataValueEditing ?? false,
        scoreTemplates: checksConfig.scoreTemplates ?? [],
        checkOrder: checksConfig.checkOrder ?? [],
        scoreOrder: checksConfig.scoreOrder ?? [],
      }

      fetcher.submit(
        {
          action: "SAVE_SECTION",
          organisation: data.loadedOrganisation,
          project: data.loadedProject,
          jsonPath: "$.checks",
          value: value as never,
          concurrency: data.existingConfig?.concurrency ?? "",
        },
        { method: "post", encType: "application/json" },
      )
    },
    [checksConfig, fetcher, data],
  )

  return (
    <div className="flex h-full flex-col">
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <div className="mx-auto w-full space-y-6">
            <div className="rounded-lg border p-4">
              <h3 className="mb-4 text-lg font-medium">View Settings</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="view"
                    checked={checksConfig.view === true || checksConfig.view === null}
                    onCheckedChange={(checked) =>
                      setChecksConfig((prev) => ({ ...prev, view: checked === true ? true : false }))
                    }
                  />
                  <label htmlFor="view" className="text-sm">
                    Enable Check View
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dataViewDefault"
                    checked={checksConfig.dataViewDefault === true}
                    onCheckedChange={(checked) =>
                      setChecksConfig((prev) => ({ ...prev, dataViewDefault: checked === true }))
                    }
                  />
                  <label htmlFor="dataViewDefault" className="text-sm">
                    Data View as Default (when both views enabled)
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hideVerifiedColumn"
                    checked={checksConfig.hideVerifiedColumn === true}
                    onCheckedChange={(checked) =>
                      setChecksConfig((prev) => ({ ...prev, hideVerifiedColumn: checked === true }))
                    }
                  />
                  <label htmlFor="hideVerifiedColumn" className="text-sm">
                    Hide Verified Column
                  </label>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4">
              <h3 className="mb-4 text-lg font-medium">Editing Settings</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="disableEvaluationEditing"
                    checked={checksConfig.disableEvaluationEditing === true}
                    onCheckedChange={(checked) =>
                      setChecksConfig((prev) => ({ ...prev, disableEvaluationEditing: checked === true }))
                    }
                  />
                  <label htmlFor="disableEvaluationEditing" className="text-sm">
                    Disable Evaluation Editing
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="disableDataValueEditing"
                    checked={checksConfig.disableDataValueEditing === true}
                    onCheckedChange={(checked) =>
                      setChecksConfig((prev) => ({ ...prev, disableDataValueEditing: checked === true }))
                    }
                  />
                  <label htmlFor="disableDataValueEditing" className="text-sm">
                    Disable Data Value Editing
                  </label>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-medium">Score Templates</h3>
                <AddThresholdDialog onAdd={handleAddThreshold} />
              </div>

              {checksConfig.scoreTemplates == null || checksConfig.scoreTemplates.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                  <p className="text-sm">No score templates configured</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {checksConfig.scoreTemplates.map((template, templateIndex) =>
                    template.thresholds?.map((threshold, thresholdIndex) => (
                      <div
                        key={`${templateIndex}-${thresholdIndex}`}
                        className="flex items-center justify-between rounded-lg border bg-muted p-4"
                      >
                        <div className="flex items-center gap-4">
                          <div className="rounded px-3 py-2 text-sm font-medium">
                            {threshold.min}% - {threshold.max}%
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{threshold.icon.iconType}</Badge>
                            <code className="rounded border border-border bg-background px-2 py-1 text-xs">
                              {threshold.icon.icon}
                            </code>
                            <div className="h-6 w-6 rounded border" style={{ backgroundColor: threshold.icon.color }} />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveThreshold(templateIndex, thresholdIndex)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    )),
                  )}
                </div>
              )}
            </div>

            <div className="rounded-lg border p-4">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Check Order</h3>
                  <p className="text-sm text-muted-foreground">If empty, checks will be sorted alphabetically</p>
                </div>
                <AddReferenceDialog
                  title="Add Check Reference"
                  type="check"
                  clientName={data.loadedOrganisation}
                  brand={data.loadedProject}
                  checkSets={checkSets}
                  isLoadingCheckSets={isLoadingCheckSets}
                  existingReferences={checksConfig.checkOrder ?? []}
                  onAdd={handleAddCheckOrder}
                />
              </div>

              {checksConfig.checkOrder == null || checksConfig.checkOrder.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                  <p className="text-sm">No check order defined (alphabetical sorting)</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {checksConfig.checkOrder.map((check, index) => {
                    const descriptions = checkDescriptions[check]
                    return (
                      <div
                        key={`${check}-${index}`}
                        className="flex items-center justify-between rounded-lg border bg-muted p-3"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="font-mono">
                            {index + 1}
                          </Badge>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{descriptions?.name ?? check}</span>
                            <span className="text-sm text-muted-foreground">
                              {descriptions?.shortDescription ?? check}
                            </span>
                            <span className="text-xs text-muted-foreground">{descriptions?.longDescription}</span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCheckOrder(index)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="rounded-lg border p-4">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Score Order</h3>
                  <p className="text-sm text-muted-foreground">If empty, scores will be sorted alphabetically</p>
                </div>
                <AddReferenceDialog
                  title="Add Score Reference"
                  type="score"
                  clientName={data.loadedOrganisation}
                  brand={data.loadedProject}
                  checkSets={checkSets}
                  isLoadingCheckSets={isLoadingCheckSets}
                  existingReferences={checksConfig.scoreOrder ?? []}
                  onAdd={handleAddScoreOrder}
                />
              </div>

              {checksConfig.scoreOrder == null || checksConfig.scoreOrder.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                  <p className="text-sm">No score order defined (alphabetical sorting)</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {checksConfig.scoreOrder.map((score, index) => {
                    const descriptions = scoreDescriptions[score]
                    return (
                      <div
                        key={`${score}-${index}`}
                        className="flex items-center justify-between rounded-lg border bg-muted p-3"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="font-mono">
                            {index + 1}
                          </Badge>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{descriptions?.name ?? score}</span>
                            <span className="text-sm text-muted-foreground">
                              {descriptions?.shortDescription ?? score}
                            </span>
                            <span className="text-xs text-muted-foreground">{descriptions?.longDescription}</span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveScoreOrder(index)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 border-t p-4">
          <div className="mx-auto w-full space-y-3">
            {fetcher.data?.errorMessage != null && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                <XCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div className="flex-1">{fetcher.data.errorMessage}</div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={fetcher.state !== "idle"} className="flex-1 bg-info hover:bg-info/90">
                {pendingAction === "SAVE_SECTION" && fetcher.state !== "idle" && (
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                <SaveIcon className="mr-2 h-4 w-4" />
                Save Checks Configuration
              </Button>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="default"
                    className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                    disabled={fetcher.state !== "idle"}
                  >
                    {pendingAction === "REMOVE_SECTION" && fetcher.state !== "idle" ? (
                      <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <XCircleIcon className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">Remove Section</span>
                    <span className="sm:hidden">Remove</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent side="top" className="w-80">
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Remove Checks Configuration?</p>
                    <p className="text-sm text-muted-foreground">
                      This will remove all checks configurations including view settings, score templates, and order
                      settings.
                    </p>
                    <div className="flex items-center justify-end gap-3">
                      <PopoverClose asChild>
                        <Button type="button" variant="ghost" size="sm">
                          Cancel
                        </Button>
                      </PopoverClose>
                      <PopoverClose asChild>
                        <Button type="button" variant="destructive" size="sm" onClick={() => void handleRemove()}>
                          Remove
                        </Button>
                      </PopoverClose>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
